// Browser-safe (contracts import it): no Nest, no Medplum, no I/O.
//
// The specialties a referral can target, keyed by the primary NUCC code the
// RequestGroup stores. NPPES only searches by taxonomy DESCRIPTION, in its own
// wording ("Cardiovascular Disease", not "Cardiology"), and one specialty is often
// filed under several codes (Geriatrics under Internal AND Family Medicine), so each
// row carries the search terms and the code family a matching doctor may hold.
export type ReferralSpecialty = {
  code: string
  label: string
  /** NPPES `taxonomy_description` values to query — one NPPES call each. */
  nppesTerms: readonly string[]
  /** A doctor matches when any of their taxonomies is one of these codes. */
  acceptedCodes: readonly string[]
  /** Appended to the Google Places text query. */
  placesPhrase: string
}

export const REFERRAL_SPECIALTIES = [
  {
    code: '207K00000X',
    label: 'Allergy & Immunology',
    nppesTerms: ['Allergy & Immunology'],
    acceptedCodes: ['207K00000X', '207KA0200X', '207KI0005X'],
    placesPhrase: 'allergist',
  },
  {
    code: '207RC0000X',
    label: 'Cardiology',
    nppesTerms: ['Cardiovascular Disease'],
    acceptedCodes: ['207RC0000X'],
    placesPhrase: 'cardiologist',
  },
  {
    code: '207N00000X',
    label: 'Dermatology',
    nppesTerms: ['Dermatology'],
    acceptedCodes: ['207N00000X'],
    placesPhrase: 'dermatologist',
  },
  {
    code: '207RE0101X',
    label: 'Endocrinology',
    nppesTerms: ['Endocrinology'],
    acceptedCodes: ['207RE0101X'],
    placesPhrase: 'endocrinologist',
  },
  {
    code: '207Y00000X',
    label: 'ENT / Otolaryngology',
    nppesTerms: ['Otolaryngology'],
    acceptedCodes: ['207Y00000X'],
    placesPhrase: 'ENT doctor',
  },
  {
    code: '207Q00000X',
    label: 'Family Medicine',
    nppesTerms: ['Family Medicine'],
    acceptedCodes: ['207Q00000X'],
    placesPhrase: 'family medicine doctor',
  },
  {
    code: '207RG0100X',
    label: 'Gastroenterology',
    nppesTerms: ['Gastroenterology'],
    acceptedCodes: ['207RG0100X'],
    placesPhrase: 'gastroenterologist',
  },
  {
    code: '208600000X',
    label: 'General Surgery',
    nppesTerms: ['Surgery'],
    acceptedCodes: ['208600000X'],
    placesPhrase: 'general surgeon',
  },
  {
    code: '207QG0300X',
    label: 'Geriatrics',
    nppesTerms: ['Geriatric Medicine'],
    acceptedCodes: ['207QG0300X', '207RG0300X'],
    placesPhrase: 'geriatrician',
  },
  {
    code: '207RH0000X',
    label: 'Hematology',
    nppesTerms: ['Hematology'],
    acceptedCodes: ['207RH0000X', '207RH0003X'],
    placesPhrase: 'hematologist',
  },
  {
    code: '207RX0202X',
    label: 'Hematology / Oncology',
    nppesTerms: ['Medical Oncology', 'Hematology & Oncology'],
    acceptedCodes: ['207RX0202X', '207RH0003X'],
    placesPhrase: 'oncologist',
  },
  {
    code: '207RI0200X',
    label: 'Infectious Disease',
    nppesTerms: ['Infectious Disease'],
    acceptedCodes: ['207RI0200X'],
    placesPhrase: 'infectious disease doctor',
  },
  {
    code: '207R00000X',
    label: 'Internal Medicine',
    nppesTerms: ['Internal Medicine'],
    acceptedCodes: ['207R00000X'],
    placesPhrase: 'internal medicine doctor',
  },
  {
    code: '207RN0300X',
    label: 'Nephrology',
    nppesTerms: ['Nephrology'],
    acceptedCodes: ['207RN0300X'],
    placesPhrase: 'nephrologist',
  },
  {
    code: '2084N0400X',
    label: 'Neurology',
    nppesTerms: ['Neurology'],
    acceptedCodes: ['2084N0400X'],
    placesPhrase: 'neurologist',
  },
  {
    code: '207V00000X',
    label: 'Obstetrics & Gynecology',
    nppesTerms: ['Obstetrics & Gynecology'],
    acceptedCodes: ['207V00000X'],
    placesPhrase: 'OB-GYN',
  },
  {
    code: '207W00000X',
    label: 'Ophthalmology',
    nppesTerms: ['Ophthalmology'],
    acceptedCodes: ['207W00000X'],
    placesPhrase: 'ophthalmologist',
  },
  {
    code: '207X00000X',
    label: 'Orthopedic Surgery',
    nppesTerms: ['Orthopaedic Surgery'],
    acceptedCodes: ['207X00000X', '207XS0117X', '207XX0005X'],
    placesPhrase: 'orthopedic surgeon',
  },
  {
    code: '208VP0000X',
    label: 'Pain Medicine',
    nppesTerms: ['Pain Medicine'],
    acceptedCodes: ['208VP0000X', '208VP0014X', '207LP2900X'],
    placesPhrase: 'pain management doctor',
  },
  {
    code: '208000000X',
    label: 'Pediatrics',
    nppesTerms: ['Pediatrics'],
    acceptedCodes: ['208000000X'],
    placesPhrase: 'pediatrician',
  },
  {
    code: '208100000X',
    label: 'Physical Medicine & Rehabilitation',
    nppesTerms: ['Physical Medicine & Rehabilitation'],
    acceptedCodes: ['208100000X'],
    placesPhrase: 'physical medicine and rehabilitation doctor',
  },
  {
    code: '208200000X',
    label: 'Plastic Surgery',
    nppesTerms: ['Plastic Surgery'],
    acceptedCodes: ['208200000X'],
    placesPhrase: 'plastic surgeon',
  },
  {
    code: '213E00000X',
    label: 'Podiatry',
    nppesTerms: ['Podiatrist'],
    acceptedCodes: ['213E00000X', '213ES0103X'],
    placesPhrase: 'podiatrist',
  },
  {
    code: '2084P0800X',
    label: 'Psychiatry',
    nppesTerms: ['Psychiatry'],
    acceptedCodes: ['2084P0800X'],
    placesPhrase: 'psychiatrist',
  },
  {
    code: '207RP1001X',
    label: 'Pulmonology',
    nppesTerms: ['Pulmonary Disease'],
    acceptedCodes: ['207RP1001X'],
    placesPhrase: 'pulmonologist',
  },
  {
    code: '2085R0001X',
    label: 'Radiation Oncology',
    nppesTerms: ['Radiation Oncology'],
    acceptedCodes: ['2085R0001X'],
    placesPhrase: 'radiation oncologist',
  },
  {
    code: '207RR0500X',
    label: 'Rheumatology',
    nppesTerms: ['Rheumatology'],
    acceptedCodes: ['207RR0500X'],
    placesPhrase: 'rheumatologist',
  },
  {
    code: '207RS0012X',
    label: 'Sleep Medicine',
    nppesTerms: ['Sleep Medicine'],
    acceptedCodes: ['207RS0012X', '2084S0012X'],
    placesPhrase: 'sleep medicine doctor',
  },
  {
    code: '207RS0010X',
    label: 'Sports Medicine',
    nppesTerms: ['Sports Medicine'],
    acceptedCodes: ['207RS0010X', '207QS0010X', '207XX0005X'],
    placesPhrase: 'sports medicine doctor',
  },
  {
    code: '208800000X',
    label: 'Urology',
    nppesTerms: ['Urology'],
    acceptedCodes: ['208800000X'],
    placesPhrase: 'urologist',
  },
  {
    code: '2086S0129X',
    label: 'Vascular Surgery',
    nppesTerms: ['Vascular Surgery'],
    acceptedCodes: ['2086S0129X'],
    placesPhrase: 'vascular surgeon',
  },
] as const satisfies readonly ReferralSpecialty[]

export type ReferralSpecialtyCode = (typeof REFERRAL_SPECIALTIES)[number]['code']

// A non-empty tuple, so contracts can pass it straight to z.enum().
export const REFERRAL_SPECIALTY_CODES = REFERRAL_SPECIALTIES.map((s) => s.code) as [
  ReferralSpecialtyCode,
  ...ReferralSpecialtyCode[],
]

export function specialtyByCode(code: string): ReferralSpecialty | undefined {
  return REFERRAL_SPECIALTIES.find((s) => s.code === code)
}
